import React, { useCallback, useRef } from "react";
import { useAppSelector } from "../../app";

const X_SCROLL = 50;
const Y_SCROLL = 50;
const SCROLL_THROTTLE_MS = 100; // Throttle scroll calls to every 100ms
const EDGE_THRESHOLD = 5; // Pixels from edge to trigger scroll

const useAutoScroll = () => {
  const { isDragging } = useAppSelector((state) => state.annotationReducer);
  const lastScrollTimeRef = useRef<number>(0);
  const scrollFrameRef = useRef<number | null>(null);

  return useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging) {
        return;
      }

      const now = Date.now();

      // Throttle scroll calls to prevent overwhelming the browser
      if (now - lastScrollTimeRef.current < SCROLL_THROTTLE_MS) {
        return;
      }

      // Cancel any pending scroll frame
      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current);
      }

      scrollFrameRef.current = requestAnimationFrame(() => {
        // Safety check: element might be unmounted by the time animation frame executes
        if (!event.currentTarget) {
          scrollFrameRef.current = null;
          return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        const { width, height } = rect;

        let scrollX = 0;
        let scrollY = 0;

        // Calculate scroll direction and amount
        if (y <= EDGE_THRESHOLD) {
          scrollY = -Y_SCROLL;
        } else if (y >= height - EDGE_THRESHOLD) {
          scrollY = Y_SCROLL;
        }

        if (x <= EDGE_THRESHOLD) {
          scrollX = -X_SCROLL;
        } else if (x >= width - EDGE_THRESHOLD) {
          scrollX = X_SCROLL;
        }

        // Only scroll if needed
        if (scrollX !== 0 || scrollY !== 0) {
          // Use 'auto' behavior to avoid conflicts with smooth scrolls
          event.currentTarget.scrollBy({
            top: scrollY,
            left: scrollX,
            behavior: "auto",
          });

          lastScrollTimeRef.current = now;
        }

        scrollFrameRef.current = null;
      });
    },
    [isDragging],
  );
};

export default useAutoScroll;
